import { ENV } from '../../config/runtimeEnv';

//Types
import { SeatUsage, TeamMember } from "../../types/admin/TeamTypes";
import { PractitionerColorPaletteResponse } from "../../types/admin/PractitionerColorTypes";
import { loadUserPreferences } from '../../utils/userPrefs/UserPrefs';
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";


const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;
const TEAM_MEMBERS_PAGE_SIZE = loadUserPreferences().tableRowsPerPage;


export const fetchTeamMembers = async (
	page: number,
	perPage: number,
	search?: string,
): Promise<{
	data: TeamMember[];
	total: number;
}> => {
	const response = await http.get(`${API_BASE_URL}/admin/staff`, {
		params: {
			page,
			per_page: perPage,
			...(search ? { search } : {}),
		},
	});
	return {
		data: response.data.data,
		total: response.data.pagination.total_items,
	};
};

/**
 * Fetch all team mebers`
 */
export const fetchAllTeamMembers = async (
	search?: string,
): Promise<{ data: TeamMember[] }> => {
	const first = await fetchTeamMembers(1, TEAM_MEMBERS_PAGE_SIZE, search);
	const all = [...first.data];

	const totalPages = Math.max(1, Math.ceil(first.total / TEAM_MEMBERS_PAGE_SIZE));
	const pagesToFetch = totalPages;

	if (pagesToFetch > 1) {
		try {
			const remainingPages = await Promise.all(
				Array.from({ length: pagesToFetch - 1 }, (_, i) => fetchTeamMembers(i + 2, TEAM_MEMBERS_PAGE_SIZE, search)),
			);
			for (const page of remainingPages) {
				all.push(...page.data);
			}
		} catch {

			console.warn("teamApi.fetchAllTeamMembers.page_fetch_failed", {
				totalPages,
				pagesFetched: 1,
			});
			return { data: all };
		}
	}

	return { data: all };
};

export const fetchTeamMember = async (
	id: UserUuid,

): Promise<{
	data: TeamMember;

}> => {
	const response = await http.get(`${API_BASE_URL}/admin/staff/${id}`);
	return {
		data: response.data,
	};
};

// The single source of truth for the practitioner calendar-colour palette (S2, D6) — this
// frontend must never keep its own hardcoded copy of the 25 accent colours.
export const fetchPractitionerColorPalette = async (): Promise<PractitionerColorPaletteResponse> => {
	const response = await http.get(`${API_BASE_URL}/admin/staff/color-palette`);
	return response.data;
};

export const updateTeamMember = async (
	id: UserUuid,
	teamMemberData: TeamMember
): Promise<TeamMember> => {
	const response = await http.put(`${API_BASE_URL}/admin/staff/${id}`, teamMemberData, {
		headers: {
			"Content-Type": "application/json",
		},
	});
	return response.data; // Assumes the backend returns the updated user with practitioner data
};

// The caller's own professional info (contract §15.1); the admin route above refuses a self-edit.
export const updateOwnProfessionalInfo = async (teamMemberInfo: TeamMember["team_member_info"]): Promise<TeamMember> =>
	(await http.put(`${API_BASE_URL}/user/professional`, { team_member_info: teamMemberInfo })).data;

// Authoritative seat usage for this clinic. The server is the only source of truth here —
// it is the same service that gates staff creation, so the counter can never disagree with
// the gate. `enforced: false` means no seat limit applies.
export const fetchSeatUsage = async (): Promise<SeatUsage> => {
	const response = await http.get(`${API_BASE_URL}/admin/seats`);
	return response.data;
};

export const createTeamMember = async (teamMemberData: any): Promise<TeamMember> => {
	const response = await http.post(`${API_BASE_URL}/admin/staff`, teamMemberData, {
		headers: {
			"Content-Type": "application/json",
		},
	});
	return response.data;
};




// Assign practitioners to a user
export const assignPractitioners = async (userId: UserUuid, practitionerId: UserUuid, roles: string[]) => {
	try {
		const response = await http.post(`${API_BASE_URL}/users/${userId}/practitioners/assign`, {
			practitioner_id: practitionerId,
			role: roles
		}, {
			headers: {
				'Content-Type': 'application/json', // Adjust based on your auth setup
			},
		});
		return response.data;
	} catch (error: any) {
		throw error.response?.data || 'Error assigning practitioners';
	}
};

// Unassign a practitioner from a user
export const unassignPractitioner = async (userId: UserUuid, practitionerId: UserUuid) => {
	try {
		const response = await http.post(`${API_BASE_URL}/users/${userId}/practitioners/unassign`, {
			practitioner_id: practitionerId
		}, {
			headers: {
				'Content-Type': 'application/json', // Adjust based on your auth setup
			},
		});
		return response.data;
	} catch (error: any) {
		throw error.response?.data || 'Error unassigning practitioner';
	}
};

// Get all practitioners assigned to a user
export const getAssignedPractitioners = async (userId: UserUuid) => {
	try {
		const response = await http.get(`${API_BASE_URL}/users/${userId}/practitioners`);
		return response.data.practitioners as TeamMember[];
	} catch (error: any) {
		throw error.response?.data || 'Error fetching assigned practitioners';
	}
};
